import { useImperativeHandle, useRef, useState } from 'react';
import type { FormEvent, Ref } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import {
  createIncident,
  locateCallerForRequestedIncident,
  startChatWithCaller,
  startVideoChatWithCaller,
  type RequestedIncidentDetails,
  type RequestedIncidentLocation,
  type RequestedIncidentPurpose,
} from '@ngsos/portal-core/commands';
import { PhoneNumber } from '@ngsos/portal-core/incidents';

import { useAgencyConfiguration } from '@/app/shell/agencyConfiguration';
import type { CreateIncidentDialogModel } from '@/features/create-incident/createIncidentRedirect';
import { incidentDetailRoute } from '@/features/shared/routes';
import { DebugView, NgButton, NgModal, useDebugMode } from '@/ui';
import type { NgModalHandle } from '@/ui';
import { useEditForm } from '@/ui/forms/useEditForm';
import { CreateIncidentLocationPreview } from './CreateIncidentLocationPreview';

export type NewIncidentDialogAction = 'create' | 'locate' | 'chat' | 'videoChat';

const FORM_ID = 'new-incident-request-form';

type DialogState = {
  requestedIncidentId: string;
  phoneNumber: string;
  callerName?: string;
  label?: string;
  summary?: string;
  location?: RequestedIncidentLocation;
  purpose: RequestedIncidentPurpose;
};

type EditableTextField = 'callerName' | 'label' | 'summary';

function createDialogState(
  model: Partial<CreateIncidentDialogModel> | undefined,
  defaultCountryCode: string,
): DialogState {
  return {
    requestedIncidentId: model?.requestedIncidentId ?? crypto.randomUUID(),
    phoneNumber: model?.phoneNumber ?? defaultCountryCode,
    callerName: model?.callerName ?? undefined,
    label: model?.label ?? undefined,
    summary: model?.summary ?? undefined,
    location: model?.location ?? undefined,
    purpose: 'Real',
  };
}

function trimToUndefined(value: string | undefined): string | undefined {
  const trimmed = value?.trim();
  return trimmed === '' ? undefined : trimmed;
}

function toRequestedIncidentDetails(
  state: DialogState,
  purpose: RequestedIncidentPurpose,
): RequestedIncidentDetails | undefined {
  const callerName = trimToUndefined(state.callerName);
  const label = trimToUndefined(state.label);
  const summary = trimToUndefined(state.summary);

  if (
    callerName === undefined &&
    label === undefined &&
    summary === undefined &&
    state.location === undefined &&
    purpose === 'Real'
  ) {
    return undefined;
  }

  return {
    ...(callerName === undefined ? {} : { callerName }),
    ...(label === undefined ? {} : { label }),
    ...(summary === undefined ? {} : { summary }),
    ...(state.location === undefined ? {} : { location: state.location }),
    ...(purpose === 'Real' ? {} : { purpose }),
  };
}

export type NewIncidentDialogHandle = {
  showModal: (model?: Partial<CreateIncidentDialogModel>) => void;
};

export type NewIncidentDialogProps = {
  ref?: Ref<NewIncidentDialogHandle>;
};

export function NewIncidentDialog({ ref }: NewIncidentDialogProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const agencyConfiguration = useAgencyConfiguration();
  const debugModeOn = useDebugMode();
  const defaultCountryCode = (agencyConfiguration.agencyPortalConfiguration.defaultCountryCode as string | null) ?? '';

  const modalRef = useRef<NgModalHandle>(null);
  const phoneNumberInputRef = useRef<HTMLInputElement>(null);
  const [state, setState] = useState(() => createDialogState(undefined, defaultCountryCode));
  const [locationPreviewReady, setLocationPreviewReady] = useState(false);
  const actionRef = useRef<NewIncidentDialogAction>('create');
  const createIncidentInProgressRef = useRef(false);
  const editForm = useEditForm<'phoneNumber'>();

  useImperativeHandle(
    ref,
    () => ({
      showModal: (model) => {
        const initialState = createDialogState(model, defaultCountryCode);
        setState(initialState);
        setLocationPreviewReady(false);
        editForm.reset({ phoneNumber: initialState.phoneNumber });
        modalRef.current?.show();
      },
    }),
    [defaultCountryCode, editForm],
  );

  const validateModel = (action: NewIncidentDialogAction): { phoneNumber?: string[] } => {
    if ((action === 'create' && state.phoneNumber.length > 5) || action !== 'create') {
      try {
        new PhoneNumber(state.phoneNumber);
      } catch {
        return { phoneNumber: [t('NewIncidentDialogFormModel_Validate_PhoneNumberError')] };
      }
    }

    return {};
  };

  const getNumberCollection = (action: NewIncidentDialogAction): string[] => {
    if (action === 'create' && state.phoneNumber.length <= 5) {
      return [];
    }

    return [new PhoneNumber(state.phoneNumber).value];
  };

  const createIncidentOnEms = (action: NewIncidentDialogAction) => {
    const phoneNumbers = getNumberCollection(action);
    const details = toRequestedIncidentDetails(state, debugModeOn ? state.purpose : 'Real');

    switch (action) {
      case 'create':
        return createIncident(state.requestedIncidentId, phoneNumbers, details);
      case 'locate':
        return locateCallerForRequestedIncident(state.requestedIncidentId, phoneNumbers, details);
      case 'chat':
        return startChatWithCaller(state.requestedIncidentId, phoneNumbers, details);
      case 'videoChat':
        return startVideoChatWithCaller(state.requestedIncidentId, phoneNumbers, details);
    }
  };

  const onSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const action = actionRef.current;

    if (!editForm.validate(() => validateModel(action)) || createIncidentInProgressRef.current) {
      return;
    }

    createIncidentInProgressRef.current = true;
    try {
      await createIncidentOnEms(action);
      void navigate(incidentDetailRoute(state.requestedIncidentId));
    } finally {
      createIncidentInProgressRef.current = false;
    }
  };

  const updateTextField = (field: EditableTextField, value: string) => {
    setState((current) => ({ ...current, [field]: value }));
  };

  const onShown = () => {
    phoneNumberInputRef.current?.focus();
    setLocationPreviewReady(true);
  };

  return (
    <NgModal
      ref={modalRef}
      header={t('NewIncidentDialog_Header')}
      width={state.location === undefined ? 'regular' : 'large'}
      onShown={onShown}
      testID="new-incident-dialog"
      bodyTemplate={
        <form id={FORM_ID} onSubmit={(event) => void onSubmit(event)}>
          <div style={{ minHeight: '7rem' }}>
            <label htmlFor="phone-number" className="label" data-testid="new-incident-dialog-phone-label">
              {t('NewIncidentDialog_Label_PhoneNumber')}
            </label>
            <input
              type="tel"
              id="phone-number"
              data-testid="new-incident-dialog-phone-input"
              className={editForm.fieldCssClass('phoneNumber', 'form-control')}
              value={state.phoneNumber}
              onChange={(event) => {
                const phoneNumber = event.target.value;
                setState((current) => ({ ...current, phoneNumber }));
                editForm.notifyFieldInput('phoneNumber', phoneNumber);
              }}
              onBlur={() => editForm.notifyFieldBlur('phoneNumber')}
              ref={phoneNumberInputRef}
            />
            {editForm.validationMessages('phoneNumber').map((message, index) => (
              <div key={index} className="validation-message">
                {message}
              </div>
            ))}
          </div>

          <DebugView>
            <div className="mb-3">
              <label htmlFor="incident-purpose" className="label">
                Purpose
              </label>
              <select
                id="incident-purpose"
                className="form-select"
                value={state.purpose}
                onChange={(event) => {
                  const purpose = event.target.value as RequestedIncidentPurpose;
                  setState((current) => ({ ...current, purpose }));
                }}
              >
                <option value="Real">Real</option>
                <option value="Demo">Demo</option>
                <option value="Test">Test</option>
              </select>
            </div>
          </DebugView>

          {state.callerName !== undefined && (
            <div className="mb-3">
              <label htmlFor="caller-name" className="label">
                {t('IncidentArchive_Filters_CallerName')}
              </label>
              <input
                type="text"
                id="caller-name"
                className="form-control"
                value={state.callerName}
                onChange={(event) => updateTextField('callerName', event.target.value)}
              />
            </div>
          )}

          {state.label !== undefined && (
            <div className="mb-3">
              <label htmlFor="incident-label" className="label">
                {t('Incident_InfoPanel_IncidentLabel')}
              </label>
              <input
                type="text"
                id="incident-label"
                className="form-control"
                value={state.label}
                onChange={(event) => updateTextField('label', event.target.value)}
              />
            </div>
          )}

          {state.summary !== undefined && (
            <div className="mb-3">
              <label htmlFor="incident-summary" className="label">
                {t('Incident_InfoPanel_CallerNotes')}
              </label>
              <textarea
                id="incident-summary"
                className="form-control"
                value={state.summary}
                onChange={(event) => updateTextField('summary', event.target.value)}
                rows={3}
              />
            </div>
          )}

          {locationPreviewReady && state.location !== undefined && (
            <CreateIncidentLocationPreview location={state.location} />
          )}
        </form>
      }
      footerTemplate={
        <>
          <NgButton
            submitButton
            form={FORM_ID}
            icon="fal fa-plus-circle"
            text={t('NewIncidentDialog_Button_Create')}
            testID="new-incident-dialog-create-button"
            onClick={() => {
              actionRef.current = 'create';
            }}
          />

          <NgButton
            submitButton
            form={FORM_ID}
            icon="fal fa-location"
            text={t('NewIncidentDialog_Button_Localize')}
            testID="new-incident-dialog-localize-button"
            onClick={() => {
              actionRef.current = 'locate';
            }}
          />

          <NgButton
            submitButton
            form={FORM_ID}
            icon="fal fa-messages"
            text={t('NewIncidentDialog_Button_CreateWithChat')}
            testID="new-incident-dialog-chat-button"
            onClick={() => {
              actionRef.current = 'chat';
            }}
          />

          <NgButton
            submitButton
            form={FORM_ID}
            icon="fal fa-video-plus"
            text={t('NewIncidentDialog_Button_CreateWithVideo')}
            testID="new-incident-dialog-video-button"
            onClick={() => {
              actionRef.current = 'videoChat';
            }}
          />

          <NgButton text={t('Button_Cancel')} outline onClick={() => modalRef.current?.hide()} />
        </>
      }
    />
  );
}
